import { apiErrorCopy } from "../shared/api-error-copy";

let csrfToken: string | null = null;

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string, public details?: unknown) {
    super(message);
  }
}

export function setCsrfToken(value: string | null): void {
  csrfToken = value;
}

/**
 * Start-up reads that a later screen will ask for anyway. Each server round trip crosses an ocean for most
 * participants, so start-up fires independent reads together instead of one screen at a time. A warm read is
 * handed out once, only for a plain GET of the same path, and only while fresh; logout clears it.
 */
const warm = new Map<string, { promise: Promise<unknown>; at: number }>();
const WARM_MS = 20_000;

export function prefetch(paths: string[]): void {
  for (const path of paths) {
    if (warm.has(path)) continue;
    const promise = api(path);
    promise.catch(() => undefined);
    warm.set(path, { promise, at: Date.now() });
  }
}

export function clearPrefetch(): void {
  warm.clear();
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const entry = warm.get(path);
  if (entry && Object.keys(init).length === 0) {
    warm.delete(path);
    if (Date.now() - entry.at < WARM_MS) return entry.promise as Promise<T>;
  }
  const headers = new Headers(init.headers);
  if (init.body !== undefined) headers.set("Content-Type", "application/json");
  if (csrfToken && init.method && !["GET", "HEAD"].includes(init.method)) {
    headers.set("X-CSRF-Token", csrfToken);
  }
  const response = await fetch(path, { ...init, headers, credentials: "same-origin" });
  const payload = await response.json().catch(() => null) as {
    error?: { code?: string; message?: string; details?: unknown };
  } | null;
  if (!response.ok) {
    if (response.status === 401 && ["authentication_required", "invalid_session"].includes(payload?.error?.code ?? "")) {
      csrfToken = null;
      if (typeof window !== "undefined") window.dispatchEvent(new Event("perhaps:session-expired"));
    }
    throw new ApiError(
      response.status,
      payload?.error?.code ?? "request_failed",
      apiErrorCopy(payload?.error?.message ?? "请求未能完成"),
      payload?.error?.details,
    );
  }
  return payload as T;
}
